import React from "react";

const ConsciousNetworkingVideo = ({ subsection, saveProgress }) => {
  const about = (
    <div className="space-y-5 pt-2 pb-6 text-body leading-7 text-[#484848]">
      <p>
This lesson reframes networking as trust building, not event small talk. It uses a simple trust equation built from credibility, reliability, intimacy, and low self focus. It highlights patterns that make people remember you: familiarity over charisma, weak ties that unlock opportunities, follow up with personality, and a recall half life that fades in about 90 days. The rule is give first, even if it is just attention.
      </p>
    </div>
  );

const transcript = (
  <div className="pt-2 pb-6 space-y-5 text-body leading-7 text-[#323232] overflow-y-scroll font-gotham">
    <p>
      From <em>LinkedIn Local</em> to <em>TechSparks</em>, and summits of every
      shape and size, someone’s networking.
    </p>

    <p>
      Shiny booths, branded mugs, awkward smiles. Everyone’s either pitching,
      posting, or praying their small talk doesn’t sound like a sales call.
    </p>

    <p>
      But let’s be honest, most people don’t network. They’re just{" "}
      <em>collecting Wi-Fi passwords with eye contact.</em>
    </p>

    <p>
      They remember your name only when they need a favour. They show up for
      your launch, but forget your follow-up.
    </p>

    <p>
      If you’ve ever stood at one of those events clutching the notepad you’re
      never going to use, wondering what exactly you’re supposed to say, this
      one’s for you.
    </p>

    <p>
      Real networking isn’t random, it’s conscious, strategic, patterned and
      built on trust.
    </p>

    <p>
      At top consulting firms, they describe trust with a simple equation:
      <br />
      <strong>
        Trust = (Credibility + Reliability + Intimacy) / Self-orientation
      </strong>
    </p>

    <p>And that, my friend, is your networking cheat code.</p>

    <ul className="list-disc pl-6 space-y-2">
      <li>
        <strong>Credibility:</strong> You prove what you stand for through
        consistent work, real results, and earned expertise.
      </li>
      <li>
        <strong>Reliability:</strong> You take ownership, deliver on time, and
        communicate early when you can’t.
      </li>
      <li>
        <strong>Intimacy:</strong> You build closeness by sharing space, whether
        it’s a desk, a chat, or a simple note that says, “Congratulations”
      </li>
      <li>
        <strong>Self-orientation:</strong> When you’re too focused on yourself,
        there’s no room to understand others.
      </li>
    </ul>

    <p>
      So if your trust score’s dropping, check where you’re over-indexing: too
      self-focused, or not connected enough.
    </p>

    <p>
      Networking isn’t about who collected the most business cards. It’s about
      creating <strong>mutual value.</strong>
    </p>

    <p>
      Think of it as a <strong>give-and-take economy</strong>, except, the give
      always comes first.
    </p>

    <p>
      You share ideas, make intros, celebrate someone else’s success, and not
      just with a thumbs-up emoji.
    </p>

    <p>
      Because <strong>the more you give, the more ability you have to take</strong>,
      not out of obligation, but out of trust.
    </p>

    <p>To get there, you need to keep a few things in mind:</p>

    <ol className="list-decimal pl-6 space-y-3 pl-4">
      <li>
        <strong>Familiarity Beats Charisma:</strong> People trust what they keep
        seeing. Show up in their inbox, their calls, their projects,
        predictably, not dramatically. 
        <br />
        <p>Familiarity builds comfort. Comfort
        builds access.</p>
      </li>

      <li>
        <strong>Weak Ties Win:</strong> Your next big opportunity won’t come
        from your work bestie. It’ll come from someone you met once at a
        conference buffet who still remembers your joke about bad paneer.
        <br />
        Sociologist Mark Granovetter proved it decades ago:{" "}
        <em>weak ties move careers more than strong ones.</em> Keep them warm.
        A quick “Thought you’d like this” ping every few months is all it
        takes.
      </li>

      <li>
        <strong>Follow-up with Personality:</strong> Everyone says “follow up,”
        but tone is everything. If your message reads like a to-do list, it
        dies there. Instead, bring it back to something unique to your
        conversation.
      </li>

      <li>
        <strong>Networking Has a Half-Life:</strong> Your professional recall
        expires in about 90 days. If they haven’t heard from you in a quarter,
        you’ve vanished. Drop a light check-in, an article, or a quick “Saw
        this and thought of you.” Stay remembered, not forgotten.
      </li>
    </ol>

    <p>
      If you think you have nothing to give, you are wrong, you can always give
      your attention to someone. Attention is the most valued currency
      nowadays. You being attentive is equal to you being not self oriented
    </p>

    <p>
      Conscious networking isn’t about who you know, it’s about who trusts you
      enough to open a door you might not even know existed.
    </p>

    <p>
      And remember, give first.
      <br />
      Because in networking, the givers own the room, the takers just borrow the
      Wi-Fi.
    </p>

    <p>Are you networking consciously?</p>
  </div>
);

  // const relatedLinks = (
  //   <div className="pt-2 pb-6">
  //     <ul className="list-disc list-inside text-body space-y-2">
  //       <li>
  //         <button type="button" className="text-link">
  //           Download Slides
  //         </button>
  //       </li>
  //       <li>
  //         <button type="button" className="text-link">
  //           Reference Reading: Career Transitions Report 2025
  //         </button>
  //       </li>
  //     </ul>
  //   </div>
  // );

  const notes = (
    <div className="pt-2 pb-6">
      <textarea
        rows={4}
        placeholder="Write your notes here..."
        className="w-full border border-gray-200 rounded-xl p-3 text-body focus:ring-2 focus:ring-brand/40 focus:outline-none"
        onBlur={(e) => saveProgress("in_progress", { notes: e.target.value })}
      />
    </div>
  );

  return { about, transcript, notes };
};

export default ConsciousNetworkingVideo;
